import React, { ReactNode, useEffect, useRef, useState } from "react";
import { CoreButton } from "./CoreButton";
import { CoreCheckBox } from "./CoreCheckBox";
import { CoreInput } from "./CoreInput";

interface CoreDataGridProps<T> {
    showSearch: boolean;
    showCheckBox: boolean;
    showEdit: boolean;
    showNew: boolean;
    showRemove: boolean;
    headerSize: number;
    cellSize: number;
    itemsKey: string;
    dictionary: DictionaryProp;
    items: T[],
    onClick?: (item: T) => any;
    onClickNew?: () => any;
    onClickEdit?: (item: T) => any;
    onClickRemove?: (item: T) => any;
    onSelected?: (items: T[]) => any;
    customActions?: React.ComponentType<any>;
}

interface IGridDictionary {
    header?: string;
    sort?: boolean;
    template?: React.ComponentType<any>;
    search?: boolean;
    order?: number;
    freeze?: boolean;
    width?: number;
    textAlign?: "start" | "center" | "end";
    hidden?: boolean;
    whiteSpace?: "nowrap" | "normal";
    formatDataValue?: (item: any) => any;
}

interface IGridInternalState {
    searchTerm: string,
    checkedAll: boolean
}

type DictionaryProp = { [x: string]: IGridDictionary };



const defaultItemsKey = "city";
const defaultItems: any[] = [
    {
        city: "Manaus",
        state: "Amazonas",
        population: "2.2m",
        order: "1",
    },

    {
        city: "Parintins",
        state: "Amazonas",
        population: "115k",
        order: "2",
    },

    {
        city: "Itacoatiara",
        state: "Amazonas",
        population: "112k",
        order: "3",
    },
];
const defaultDictionary: DictionaryProp = {
    city: {
        header: "City",
        sort: true,
        search: true,
    },
    state: {
        header: "State",
        sort: false,
        search: true,
    },
    population: {
        header: "Population",
        sort: true,
    },
    order: {
        header: "Order",
        sort: false,
    },


}

interface InternalItem {
    _selected: boolean;
}


export const CoreDataGrid = <T extends Object>(
    {
        cellSize = 14,
        headerSize = 14,
        showCheckBox = true,
        dictionary = defaultDictionary,
        itemsKey = defaultItemsKey,
        items = defaultItems,
        showSearch = true,
        showEdit = true,
        showNew = true,
        showRemove = true,
        onClick,
        onClickNew,
        onClickEdit,
        onClickRemove,
        onSelected,
        customActions

    }: CoreDataGridProps<T>
) => {

    let [iArray, setIArray] = useState<T[]>([...items]);
    console.log("init", { iArray });
    const getSelectedItems = (): T[] => {
        return (iArray as any[]).filter(i => i._selected);
    }


    const [iState, setIState] = useState<IGridInternalState>({
        searchTerm: "",
        checkedAll: false
    })



    const iOnClick = (item: T) => {

        let index = iArray.findIndex(i => i == item);
        (iArray as any[])[index]._selected = !(iArray as any[])[index]._selected;
        console.log("selected", index, iArray[index]);
        dispatchOnSelected();

        onClick && onClick({ ...item });

        setIArray([...iArray]);
    }


    const iFilterItems = (item: T, searchString: string): boolean => {

        console.log("searchString", searchString);

        if ((item as any)._selected) return true;

        if (searchString == "") return true;

        let searchKeys = Object.keys(dictionary).filter(k => dictionary[k].search);

        // search for all searchable keys sent by the user
        let result = false;

        for (let index = 0; index < searchKeys.length; index++) {
            const searchKey = searchKeys[index];
            if (
                item[searchKey]
                    .toString()
                    .toLowerCase()
                    .indexOf(searchString.toLowerCase()) !== -1
            ) {
                result = true;
                break;
            }
        }

        return result;
    };

    type sortResult = 1 | -1 | 0;
    const iSortHeader = (a: string, b: string): sortResult => {
        let aValue = dictionary[a].order || 0;
        let bValue = dictionary[b].order || 0;

        if (aValue < bValue) return -1;
        if (aValue > bValue) return 1;
        return 0;
    };

    const iNotHiddenColumn = (dictionaryKey: string): boolean => !dictionary[dictionaryKey].hidden;

    const onSearchChange = (event: CoreEventChange) => {
        setIState({ ...iState, searchTerm: event.value as string })
    }

    const dispatchOnSelected = () => {
        onSelected && onSelected(getSelectedItems());
    }

    const onCheckAllChange = (event: CoreEventChange) => {
        let value = event.checked;

        let newArray = (iArray as any[])
            .map(i => {
                if (iFilterItems(i, iState.searchTerm))
                    i._selected = value;
                return i;
            })

        setIState({ ...iState, checkedAll: value });
        setIArray([...newArray]);

        dispatchOnSelected();

    }

    const setOnClickEdit = () => {
        if (onClickEdit) {

            let selectedIndex = (iArray as any[]).findIndex(i => i._selected);
            let selectedItem = iArray[selectedIndex];
            onClickEdit(selectedItem);
        }
    }

    const setOnClickRemove = () => {
        if (onClickRemove) {
            let selectedIndex = (iArray as any[]).findIndex(i => i._selected);
            let selectedItem = iArray[selectedIndex];
            onClickRemove(selectedItem);
        }
    }

    let CustomActions = customActions;

    return (
        <div data-grid className="flex flex-col overflow-hidden" >
            <div data-grid-bar className="grid grid-cols-2 gap-10 py-2 items-end flex">
                {showSearch && <CoreInput
                    name="search"
                    label="Filtrar"
                    group="data-grid"
                    value={iState.searchTerm}
                    onChange={onSearchChange}
                    type="text" />
                }

                <div data-grid-actions className="grid grid-flow-col gap-3 auto-cols-max justify-end" >
                    {customActions && <CustomActions />}
                    {showEdit && <CoreButton onClick={setOnClickEdit} text="Editar" kind="normal" schema="warning" disabled={getSelectedItems().length != 1} />}
                    {showRemove && <CoreButton onClick={setOnClickRemove} text="Remover" schema="danger" kind="normal" disabled={getSelectedItems().length != 1} />}
                    {showNew && <CoreButton text="Novo" schema="success" />}
                </div>
            </div>
            <div className="overflow-scroll flex" >
                <table className="border-separate w-full rounded-md" style={{ borderSpacing: "0px 4px" }}>
                    <thead>
                        <tr className="sticky top-0 z-10 bg-white h-14 shadow-md rounded-md">
                            {showCheckBox &&
                                <th className="px-2 cursor-pointer first:rounded-l-md last:rounded-r-md" >
                                    <CoreCheckBox
                                        name={`data-grid-check-all`}
                                        checked={iState.checkedAll}
                                        onChange={onCheckAllChange}
                                    />
                                </th>
                            }
                            {
                                Object.keys(dictionary)
                                    .sort(iSortHeader)
                                    .filter(iNotHiddenColumn).map((dkey, dIndex) => {
                                        return (
                                            <th key={dIndex} className="text-sm font-semibold first:rounded-l-md last:rounded-r-md text-gray-400">{dictionary[dkey].header || dkey}</th>
                                        )
                                    })
                            }
                        </tr>
                    </thead>
                    <tbody>
                        {(iArray as any[]).filter(item => iFilterItems(item, iState.searchTerm))
                            .map((item, rowIndex) => {
                                return (
                                    <tr key={`r-${rowIndex}`}
                                        className={`
                                       
                                        cursor-pointer 
                                        ${item._selected ? 'bg-emerald-50 hover:bg-emerald-50' : 'bg-gray-50 hover:bg-white '}
                                        animate-slidedown
                                        hover:shadow-lg 
                                        z-20 hover:z-30 
                                        transition-shadow`}
                                        onClick={(e) => { e.stopPropagation(); iOnClick(item); }}>
                                        {showCheckBox &&
                                            <td className={`px-2 first:rounded-l-sm last:rounded-r-sm border-l-4 ${item._selected ? 'border-lime-500' : 'border-transparent'}`}>
                                                <CoreCheckBox
                                                    name={`r-${rowIndex}-cell`}
                                                    checked={item._selected}
                                                    onChange={() => iOnClick(item)}
                                                />
                                            </td>
                                        }

                                        {
                                            Object.keys(dictionary)
                                                .sort(iSortHeader)
                                                .filter(iNotHiddenColumn).map((dKey, dIndex) => {
                                                    let Component = dictionary[dKey].template;
                                                    let formatValue = dictionary[dKey].formatDataValue;
                                                    return (
                                                        <td key={dIndex} className="text-sm font-normal text-gray-500 first:rounded-l-sm last:rounded-r-sm">
                                                            {
                                                                Component ?
                                                                    <Component value={item[dKey]} />
                                                                    :
                                                                    formatValue ?
                                                                        formatValue(item[dKey])
                                                                        :
                                                                        item[dKey]
                                                            }
                                                        </td>
                                                    )
                                                })
                                        }
                                    </tr>
                                )
                            })
                        }
                    </tbody>
                </table>

            </div>
        </div>
    )

}